export function createWorkbenchViewController({
  elements,
  stores,
  getWorkspaceState,
  getRoleCardsVisible,
  renderers,
  loadMemoryItems,
  initialCollapsed = false,
  onCollapsedChange,
  documentRef = document,
} = {}) {
  const {
    artifactList, canvas, skillsList, skillsItems, toolsItems, memoryList,
    workbench, workbenchToggle,
  } = elements || {};
  const { artifactStore, runStore, memoryStore, canvasStore, sessionStore } = stores || {};
  const {
    renderArtifacts, renderCanvas, renderSkills, renderMemoryItems, renderRuns, renderTools,
  } = renderers || {};
  let collapsed = Boolean(initialCollapsed);
  let bound = false;

  function isActive(selector) {
    return documentRef?.querySelector(selector)?.classList.contains('active') === true;
  }

  function setCollapsed(nextCollapsed) {
    collapsed = Boolean(nextCollapsed);
    if (!workbench) return;
    workbench.classList.toggle('aichat-workbench--collapsed', collapsed);
    workbenchToggle?.setAttribute('aria-expanded', String(!collapsed));
    if (workbenchToggle) workbenchToggle.title = collapsed ? '展开工作台' : '收起工作台';
    onCollapsedChange?.(collapsed);
  }

  function renderActivePanel() {
    if (!workbench || workbench.hidden) return;
    if (isActive("[data-workbench-tab='canvas']")) return void renderCanvas?.(canvasStore.getCanvasState());
    if (isActive("[data-workbench-tab='skills']")) return void renderSkills?.();
    if (isActive("[data-workbench-tab='memory']")) return void renderMemoryItems?.(memoryStore.getMemoryState());
    if (documentRef?.querySelector('[data-material-tab].active')?.dataset.materialTab === 'tasks') {
      return void renderRuns?.(runStore.getRunState());
    }
    renderArtifacts?.(artifactStore.getArtifactState());
  }

  function switchWorkbench(tab) {
    const isCanvas = tab === 'canvas';
    const isSkills = tab === 'skills';
    const isMemory = tab === 'memory';
    documentRef?.querySelectorAll('[data-workbench-tab]').forEach((button) => {
      const active = button.dataset.workbenchTab === tab;
      button.classList.toggle('active', active);
      button.setAttribute('aria-selected', String(active));
    });
    if (artifactList) artifactList.hidden = isCanvas || isSkills || isMemory;
    if (canvas) canvas.hidden = !isCanvas;
    if (skillsList) skillsList.hidden = !isSkills;
    if (memoryList) memoryList.hidden = !isMemory;
    documentRef?.querySelector('.aichat-workbench-subtabs')?.toggleAttribute('hidden', isCanvas || isSkills || isMemory);
    if (isSkills && toolsItems && !toolsItems.hasChildNodes()) renderTools?.();
    if (isMemory && !memoryStore.getMemoryState().loaded && !memoryStore.getMemoryState().loading) loadMemoryItems?.();
    renderActivePanel();
  }

  function setVisible(hasSession) {
    if (!workbench) return;
    workbench.hidden = !hasSession;
    if (hasSession) setCollapsed(collapsed);
  }

  function syncVisibility() {
    if (!workbench) return;
    if (getRoleCardsVisible?.()) {
      workbench.hidden = true;
      return;
    }
    const snapshot = getWorkspaceState?.() || {};
    const hasSession = Boolean(sessionStore.getSessionState().currentSession);
    const hasContent = hasSession && (
      snapshot.artifacts > 0 || snapshot.runs > 0 || snapshot.canvasArtifacts > 0 ||
      snapshot.installedSkills > 0 || snapshot.toolCapabilities > 0 ||
      snapshot.memoryItems > 0 || snapshot.memoryLoading || snapshot.memoryError ||
      snapshot.artifactsLoading || snapshot.artifactsError || snapshot.runsLoading || snapshot.runsError ||
      snapshot.skillsLoading || snapshot.skillsError || snapshot.toolsError
    );
    workbench.hidden = !hasContent;
    if (hasContent) setCollapsed(collapsed);
  }

  function bind() {
    if (bound) return;
    bound = true;
    workbenchToggle?.addEventListener('click', () => setCollapsed(!collapsed));
    documentRef?.querySelectorAll('[data-workbench-tab]').forEach((button) => {
      button.addEventListener('click', () => switchWorkbench(button.dataset.workbenchTab));
    });
    documentRef?.querySelectorAll('[data-material-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        documentRef.querySelectorAll('[data-material-tab]').forEach((item) => item.classList.toggle('active', item === button));
        renderActivePanel();
      });
    });
  }

  return { bind, renderActivePanel, setCollapsed, setVisible, switchWorkbench, syncVisibility };
}
